import * as React from "react"
import { router } from "@inertiajs/react"
import { Search } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { clean } from "@/components/Pagination"
import type { DeliveryFilterValues, RunDetail } from "@/types/run"

/**
 * The outcome options carry the run's own counts, so the filter itself answers
 * "how many failed?" before anyone selects anything.
 */
function outcomeOptions(run: RunDetail) {
  return [
    { value: "all", label: `Everyone (${run.matched_count.toLocaleString()})` },
    { value: "sent", label: `Sent (${run.sent_count.toLocaleString()})` },
    { value: "failed", label: `Failed (${run.failed_count.toLocaleString()})` },
    {
      value: "skipped_opted_out",
      label: `Opted out (${run.opted_out_count.toLocaleString()})`,
    },
    {
      value: "skipped_no_mobile",
      label: `No mobile number (${run.no_mobile_count.toLocaleString()})`,
    },
    {
      value: "skipped_duplicate",
      label: `Already texted that day (${run.duplicate_count.toLocaleString()})`,
    },
    {
      value: "skipped_too_long",
      label: `Too long (${run.too_long_count.toLocaleString()})`,
    },
  ]
}

export function DeliveryFilters({
  run,
  filters,
}: {
  run: RunDetail
  filters: DeliveryFilterValues
}) {
  const [term, setTerm] = React.useState(filters.q ?? "")

  // Keep the box in step when the server sends a different value back, e.g.
  // after the browser's back button.
  React.useEffect(() => {
    setTerm(filters.q ?? "")
  }, [filters.q])

  const apply = React.useCallback(
    (changes: Record<string, string | null>) => {
      router.get(
        `/runs/${run.id}`,
        clean({
          outcome: filters.outcome === "all" ? null : filters.outcome,
          q: filters.q,
          ...changes,
        }),
        { preserveState: true, preserveScroll: true, replace: true },
      )
    },
    [run.id, filters.outcome, filters.q],
  )

  // Debounced so typing a name does not fire a query per keystroke against a
  // table with ten thousand rows in it.
  React.useEffect(() => {
    const current = filters.q ?? ""
    if (term === current) return

    const timer = window.setTimeout(() => apply({ q: term || null }), 300)
    return () => window.clearTimeout(timer)
  }, [term, filters.q, apply])

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div>
        <label htmlFor="filter_outcome">Outcome</label>
        <div className="mt-1">
          <Select
            id="filter_outcome"
            value={filters.outcome}
            onChange={(event) =>
              apply({
                outcome: event.target.value === "all" ? null : event.target.value,
              })
            }
          >
            {outcomeOptions(run).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <div>
        <label htmlFor="filter_q">Find a member</label>
        <div className="relative mt-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
          <Input
            id="filter_q"
            className="pl-9"
            value={term}
            placeholder="Name, number, or member id"
            onChange={(event) => setTerm(event.target.value)}
          />
        </div>
      </div>
    </div>
  )
}
